import { Link, getRouteApi, useBlocker, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ENTITY_TYPE_NAMES,
  JAVASCRIPT,
  SCRIPT_NAME_MAX,
  languageName,
  writeScriptPack,
  type GeneratedEntry,
  type SavedScript,
} from '@realmery/realm-core';
import { realms } from '../../db/client.ts';
import { realmFile } from '../../db/protocol.ts';
import { saveBytes } from '../../files.ts';
import { runScript } from '../../scripting/runScript.ts';
import type { RunResult } from '../../scripting/scriptRunner.ts';
import { settings } from '../../settings.ts';
import { message } from '../sheets/SheetsPage.tsx';
import { fieldText, generatedEntry } from './GeneratorsPage.tsx';

/**
 * One saved script, as the desktop's script view: its source to edit, Run (in the sandbox, as
 * the editor has it, saved or not), what it printed, and - since a run creates nothing - the
 * entries it would make, to choose from and create. A Python script from the desktop is shown
 * as it is, with how to write it for the web.
 */

const route = getRouteApi('/_app/realm/$realmKey/scripts/$scriptId');
const ENTRY_TYPES = Object.keys(ENTITY_TYPE_NAMES).filter((type) => type !== 'CUSTOM');

export function ScriptPage() {
  const script = route.useLoaderData();
  const { realmKey } = route.useParams();
  if (!script) {
    return (
      <article className="document">
        <h1>No such script</h1>
        <p>
          It may have been deleted.{' '}
          <Link to="/realm/$realmKey/generators" params={{ realmKey }}>
            Generators and scripts
          </Link>
        </p>
      </article>
    );
  }
  return script.language === JAVASCRIPT ? <ScriptEditor key={script.id} script={script} /> : <ForeignScript key={script.id} script={script} />;
}

function exportScript(script: SavedScript, source: string) {
  const pack = writeScriptPack({ generators: [], scripts: [{ name: script.name, description: script.description, source, language: script.language }] }, 'Realmery Web');
  saveBytes(new TextEncoder().encode(pack), `${script.name.replace(/[^\w -]+/g, '').trim() || 'script'}.json`, 'application/json');
}

function ScriptEditor({ script }: { script: SavedScript }) {
  const { realmKey } = route.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [name, setName] = useState(script.name);
  const [description, setDescription] = useState(script.description);
  const [source, setSource] = useState(script.source);
  const [dirty, setDirty] = useState(false);
  const [seed, setSeed] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [panel, setPanel] = useState<'none' | 'delete'>('none');
  const [error, setError] = useState('');
  const blocker = useBlocker({ shouldBlockFn: () => dirty, withResolver: true, enableBeforeUnload: () => dirty });

  const edit = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setDirty(true);
  };

  async function save() {
    setError('');
    try {
      await realms.updateScript(file, script.id, name, description, source);
      setDirty(false);
      await router.invalidate();
    } catch (e) {
      setError(message(e, 'The script could not be saved.'));
    }
  }

  async function run() {
    setRunning(true);
    setResult(null);
    const outcome = await runScript(source, seed.trim() === '' ? null : seed.trim());
    setResult(outcome);
    setRunning(false);
  }

  async function remove() {
    setError('');
    try {
      setDirty(false);
      await realms.deleteScript(file, script.id);
      void navigate({ to: '/realm/$realmKey/generators', params: { realmKey }, replace: true });
    } catch (e) {
      setError(message(e, 'The script could not be deleted.'));
    }
  }

  return (
    <article className="document script-page">
      <p className="eyebrow">
        <Link to="/realm/$realmKey/generators" params={{ realmKey }}>
          ← Generators and scripts
        </Link>
      </p>
      <label className="sheet-name">
        <span className="visually-hidden">The script's name</span>
        <input value={name} maxLength={SCRIPT_NAME_MAX} onChange={(e) => edit(setName)(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <input value={description} onChange={(e) => edit(setDescription)(e.currentTarget.value)} />
      </label>
      <div className="actions">
        <button type="button" disabled={!dirty} onClick={() => void save()}>
          Save
        </button>
        <button type="button" className="quiet" onClick={() => exportScript(script, source)}>
          Export…
        </button>
        <button type="button" className="quiet" onClick={() => (settings()['general.confirmDeletes'] ? setPanel('delete') : void remove())}>
          Delete…
        </button>
        {dirty && <span className="autosave dirty">Unsaved changes</span>}
      </div>
      {panel === 'delete' && (
        <div className="panel confirm" role="alertdialog" aria-label={`Delete ${script.name}`}>
          <p>Delete the script “{script.name}”? This can't be undone.</p>
          <div className="actions">
            <button type="button" className="danger" onClick={() => void remove()}>
              Delete script
            </button>
            <button type="button" onClick={() => setPanel('none')}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {blocker.status === 'blocked' && (
        <div className="panel confirm" role="alertdialog" aria-label="Unsaved changes">
          <p>Leave without saving this script? Your changes to it will be lost.</p>
          <div className="actions">
            <button type="button" className="danger" onClick={() => blocker.proceed()}>
              Leave
            </button>
            <button type="button" onClick={() => blocker.reset()}>
              Stay
            </button>
          </div>
        </div>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      <label className="script-source">
        <span>JavaScript</span>
        <textarea
          value={source}
          rows={18}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          onChange={(e) => edit(setSource)(e.currentTarget.value)}
          onKeyDown={(e) => {
            // Tab indents, as in a code editor; Escape then Tab leaves the box.
            if (e.key !== 'Tab' || e.shiftKey) return;
            e.preventDefault();
            const box = e.currentTarget;
            const at = box.selectionStart;
            edit(setSource)(`${source.slice(0, at)}  ${source.slice(box.selectionEnd)}`);
            requestAnimationFrame(() => box.setSelectionRange(at + 2, at + 2));
          }}
        />
      </label>
      <div className="actions">
        <button type="button" className="primary" disabled={running} onClick={() => void run()}>
          {running ? 'Running…' : '▶ Run'}
        </button>
        <label className="inline-select">
          Seed
          <input className="seed" value={seed} inputMode="numeric" placeholder="any" title="The same seed makes the same run again" onChange={(e) => setSeed(e.currentTarget.value)} />
        </label>
        <span className="meta">Runs as the editor has it, saved or not. A run creates nothing until you choose.</span>
      </div>
      {result && <RunOutput result={result} file={file} realmKey={realmKey} onSeed={setSeed} />}
      <ScriptHelp />
    </article>
  );
}

function RunOutput({ result, file, realmKey, onSeed }: { result: RunResult; file: string; realmKey: string; onSeed: (seed: string) => void }) {
  const router = useRouter();
  const [chosen, setChosen] = useState<Set<number>>(() => new Set(result.entries.map((_, i) => i)));
  const [type, setType] = useState(() => (typeof result.generated?.['type'] === 'string' && ENTRY_TYPES.includes(result.generated['type'].toUpperCase()) ? result.generated['type'].toUpperCase() : 'CONCEPT'));
  const [created, setCreated] = useState<{ id: string; title: string }[] | null>(null);
  const [error, setError] = useState('');

  async function create(entries: GeneratedEntry[]) {
    setError('');
    try {
      const { ids } = await realms.createGeneratedEntries(file, entries);
      setCreated(ids.map((id, i) => ({ id, title: entries[i]!.title })));
      // The sidebar lists the new entries.
      await router.invalidate();
    } catch (e) {
      setError(message(e, 'The entries could not be made.'));
    }
  }

  return (
    <section className="panel run-output" aria-label="Output">
      <p className="meta">
        {result.ok ? 'Ran' : 'Stopped'} with seed {result.seed}{' '}
        <button type="button" className="quiet" title="Use this seed to make the same run again" onClick={() => onSeed(result.seed)}>
          Use again
        </button>
      </p>
      {result.log && <pre className="code-view" aria-label="What the script logged">{result.log}</pre>}
      {result.error && (
        <p className="notice-error" role="alert">
          {result.error}
        </p>
      )}
      {result.ok && !result.log && result.entries.length === 0 && !result.generated && <p className="meta">The script ran with no output.</p>}

      {result.generated && !created && (
        <div className="generated">
          <h3>generate() made</h3>
          <dl className="fields">
            {Object.entries(result.generated).map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{fieldText(value)}</dd>
              </div>
            ))}
          </dl>
          <div className="actions">
            <label className="inline-select">
              As a
              <select value={type} onChange={(e) => setType(e.currentTarget.value)}>
                {ENTRY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ENTITY_TYPE_NAMES[t]}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="primary" onClick={() => void create([generatedEntry(type, result.generated!)])}>
              + Create entry
            </button>
          </div>
        </div>
      )}

      {result.entries.length > 0 && !created && (
        <div className="to-create">
          <h3>
            The run would make {result.entries.length} {result.entries.length === 1 ? 'entry' : 'entries'}
          </h3>
          <table className="table-rows">
            <thead>
              <tr>
                <th scope="col">
                  <span className="visually-hidden">Create</span>
                </th>
                <th scope="col">Title</th>
                <th scope="col">Type</th>
                <th scope="col">Description</th>
              </tr>
            </thead>
            <tbody>
              {result.entries.map((entry, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Create ${entry.title}`}
                      checked={chosen.has(i)}
                      onChange={() =>
                        setChosen((all) => {
                          const next = new Set(all);
                          if (!next.delete(i)) next.add(i);
                          return next;
                        })
                      }
                    />
                  </td>
                  <td>{entry.title}</td>
                  <td>
                    {entry.customTypeName ?? ENTITY_TYPE_NAMES[entry.entityType]}
                    {entry.labels.length > 0 && <span className="meta"> · {entry.labels.join(', ')}</span>}
                  </td>
                  <td>{entry.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="actions">
            <button type="button" className="primary" disabled={chosen.size === 0} onClick={() => void create(result.entries.filter((_, i) => chosen.has(i)))}>
              Create {chosen.size} {chosen.size === 1 ? 'entry' : 'entries'}
            </button>
          </div>
        </div>
      )}
      {created && (
        <p className="notice" role="status">
          Created {created.length} {created.length === 1 ? 'entry' : 'entries'}:{' '}
          {created.slice(0, 10).map((c, i) => (
            <span key={c.id}>
              {i > 0 && ', '}
              <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: c.id }} search={{}}>
                {c.title}
              </Link>
            </span>
          ))}
          {created.length > 10 && ` and ${created.length - 10} more`}.
        </p>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function ForeignScript({ script }: { script: SavedScript }) {
  const { realmKey } = route.useParams();
  return (
    <article className="document script-page">
      <p className="eyebrow">
        <Link to="/realm/$realmKey/generators" params={{ realmKey }}>
          ← Generators and scripts
        </Link>
      </p>
      <h1>{script.name}</h1>
      {script.description && <p className="lead">{script.description}</p>}
      <p className="import-note">
        This script is written in {languageName(script.language)} for the desktop app, where it runs and can be changed. Here it's shown as it is. To run it on the web, write it as a new JavaScript script:
        functions become <code>function name() {'{ ... }'}</code>, <code>f"...{'{x}'}"</code> strings become <code>`...${'{x}'}`</code>, and <code>print(...)</code> is <code>log(...)</code>. The functions it calls -{' '}
        <code>generate_city()</code>, <code>rand_choice()</code>, <code>create_entry()</code> and the rest - are the same.
      </p>
      <div className="actions">
        <button type="button" className="quiet" onClick={() => exportScript(script, script.source)}>
          Export…
        </button>
      </div>
      <pre className="code-view" aria-label="The script's source">
        {script.source}
      </pre>
    </article>
  );
}

function ScriptHelp() {
  return (
    <details className="script-help">
      <summary>What a script can use</summary>
      <ul>
        <li>
          <code>generate_name()</code>, <code>generate_city()</code>, <code>generate_npc()</code>, <code>generate_faction()</code>, <code>generate_tavern()</code>,{' '}
          <code>generate_history()</code>, <code>generate_language()</code>: the Quick Generators.
        </li>
        <li>
          <code>rand_choice(list)</code>, <code>rand_int(min, max)</code> (both ends included), <code>roll("4d6kh3")</code>: chance, from the run's seed.
        </li>
        <li>
          <code>log(...)</code> (or <code>print</code>): shown under Output.
        </li>
        <li>
          <code>create_city(x)</code>, <code>create_npc(x)</code>, <code>create_faction(x)</code>, <code>create_tavern(x)</code>, <code>create_history(x)</code>,{' '}
          <code>create_language(x)</code> and <code>create_entry(type, title, description)</code>: the entries to make, shown for you to choose.
        </li>
        <li>
          A script that defines <code>generate()</code> and creates nothing has it called, and its result shown.
        </li>
      </ul>
      <p className="meta">Scripts run in a sandbox, with no network and nothing of the page: at most 5 seconds, 32 MB of memory and 500 entries a run.</p>
    </details>
  );
}
